import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  icon?: string;
  title: string;
  width?: "compact" | "rich";
  children: ReactNode;
  footer?: ReactNode;
}

const widths: Record<NonNullable<Props["width"]>, string> = {
  compact: "w-[480px]",
  rich: "w-[600px]",
};

export function Dialog({
  open,
  onClose,
  icon,
  title,
  width = "compact",
  children,
  footer,
}: Props) {
  const { t } = useTranslation("common");
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const previousActive = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      previousActive?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 ${widths[width]} max-w-[94vw] max-h-[88vh] flex flex-col bg-surface-base border border-border-subtle rounded-sm shadow-2xl overflow-hidden`}
      >
        <header className="h-9 px-3 flex items-center justify-between border-b border-border-subtle shrink-0">
          <div className="inline-flex items-center gap-2 min-w-0">
            {icon && (
              <span
                className="material-symbols-outlined text-brand-primary text-[16px]"
                aria-hidden="true"
              >
                {icon}
              </span>
            )}
            <span className="font-sans font-bold uppercase tracking-widerx text-12 leading-[17px] text-text-main truncate">
              {title}
            </span>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("actions.close")}
            title={t("actions.close")}
            className="w-control h-control inline-flex items-center justify-center rounded-sm text-text-muted hover:text-text-main hover:bg-surface-subtle transition-colors duration-150 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <div className="p-4 flex flex-col gap-3 overflow-y-auto">{children}</div>

        {footer && (
          <footer className="h-9 px-3 flex items-center justify-between border-t border-border-subtle shrink-0">
            {footer}
          </footer>
        )}
      </div>
    </>
  );
}
